// IND-041 a IND-043: cálculos de la Hoja de recuento. VALOR y CONSUMO como el
// Excel; las sugerencias nunca se escriben solas.
import type {
  RecountCompareResult,
  RecountEstimate,
  RecountRowSource,
  RecountSheetRow,
  RecountSupplier,
} from '../services/industrialCostService';
import { esNumberFormat } from './numberFormat';

const FAR_SHARE = 0.25;
const FAR_MIN_QTY = 50;

export type RecountFilter = 'all' | 'pending' | 'counted' | 'suggested' | 'manual' | 'no_code' | 'invalid';
export type TabletFilter = 'all' | 'pending' | 'copied' | 'far';
export type TabletStatus = 'pending' | 'counted' | 'copied' | 'far';

const qty = esNumberFormat({ maximumFractionDigits: 3 });

export function rowValue(row: RecountSheetRow): number | null {
  return row.current_qty === null ? null : row.current_qty * (row.price ?? 0);
}

/** La fila guardada trae el código del libro; el que manda lo da la lectura de la hoja. */
export function mergeSavedRow(row: RecountSheetRow, saved: RecountSheetRow): RecountSheetRow {
  return { ...row, ...saved, supplier_code: row.supplier_code };
}

export function rowConsumption(row: RecountSheetRow): number | null {
  // IND-052: igual en las tres tablas del libro. IND-053: la silicona sin MES
  // ANTERIOR (el libro no lo trae) queda «—», no negativa.
  if (row.current_qty === null || (row.table_kind === 'single' && row.previous_qty === null)) return null;
  return (row.previous_qty ?? 0) + (row.purchases_qty ?? 0) - row.current_qty;
}

export function isTyped(row: RecountSheetRow, field: string): boolean {
  return row.origins[field]?.source === 'tecleado';
}

/** IND-053: lo que se pierde al sustituir la hoja. Contados = ACTUAL que no
 * viene del libro; cambios a mano = el resto de casillas tecleadas. */
export function replaceLoss(rows: RecountSheetRow[]): { counted: number; typed: number } {
  let counted = 0;
  let typed = 0;
  for (const row of rows) {
    if (row.current_qty !== null && row.origins.current_qty?.source !== 'libro') counted += 1;
    for (const [field, origin] of Object.entries(row.origins)) {
      if (field !== 'current_qty' && origin?.source === 'tecleado') typed += 1;
    }
  }
  return { counted, typed };
}

export function sheetSummary(rows: RecountSheetRow[]) {
  return {
    rows: rows.length,
    counted: rows.filter((row) => row.current_qty !== null).length,
    value: rows.reduce((total, row) => total + (rowValue(row) ?? 0), 0),
  };
}

export function matchesSearch(row: RecountSheetRow, search: string): boolean {
  const text = search.trim().toLowerCase();
  if (!text) return true;
  return [row.material_code, row.reference, row.supplier, row.destination]
    .some((value) => (value ?? '').toLowerCase().includes(text));
}

export function hasSuggestion(row: RecountSheetRow): boolean {
  return (
    (row.purchases_suggested !== null && row.purchases_suggested !== row.purchases_qty) ||
    (row.price_suggested !== null && row.price_suggested !== row.price)
  );
}

/** Proveedores distintos sin código: el aviso de arriba, una sola vez. */
export function suppliersWithoutCode(rows: RecountSheetRow[]): number {
  return new Set(
    rows
      .filter((row) => row.supplier_state === 'no_code' && row.supplier_key)
      .map((row) => row.supplier_key),
  ).size;
}

/** IND-046: el código son solo números, de 1 a 5 cifras; 0 no es un código (IND-047). */
export function isSupplierCode(value: string): boolean {
  return /^[0-9]{1,5}$/.test(value) && Number(value) > 0;
}

// IND-048: las tres tablas de la hoja STP-MP, cada una con su color y su nombre.
export type RecountTableKind = 'raw_materials' | 'packaging' | 'single';
export type RecountTable = { kind: RecountTableKind; label: string; color: string };

export const RECOUNT_TABLES: RecountTable[] = [
  { kind: 'raw_materials', label: 'Materias primas', color: '#38bdf8' },
  { kind: 'packaging', label: 'Embalaje', color: '#fbbf24' },
  { kind: 'single', label: 'Silicona', color: '#a78bfa' },
];

/** Las filas añadidas a mano y las anteriores a IND-046 cuentan como materias primas. */
export function tableOf(row: RecountSheetRow): RecountTable {
  return RECOUNT_TABLES.find((table) => table.kind === row.table_kind) ?? RECOUNT_TABLES[0];
}

export function filterByTable(rows: RecountSheetRow[], kind: RecountTableKind | 'all'): RecountSheetRow[] {
  return kind === 'all' ? rows : rows.filter((row) => tableOf(row).kind === kind);
}

/** Línea de cabecera antes de la primera fila visible de cada tabla, con sus filas visibles. */
export function tableStarts(rows: RecountSheetRow[]): Map<string, RecountTable & { count: number }> {
  const starts = new Map<string, RecountTable & { count: number }>();
  const first = new Map<RecountTableKind, string>();
  const counts = new Map<RecountTableKind, number>();
  for (const row of rows) {
    const table = tableOf(row);
    if (!first.has(table.kind)) first.set(table.kind, row.id);
    counts.set(table.kind, (counts.get(table.kind) ?? 0) + 1);
  }
  for (const table of RECOUNT_TABLES) {
    const id = first.get(table.kind);
    if (id) starts.set(id, { ...table, count: counts.get(table.kind) ?? 0 });
  }
  return starts;
}

export function supplierNote(item: RecountSupplier): string {
  const deliveries = item.has_deliveries ? 'con albaranes' : 'sin albaranes todavía';
  if (item.note === 'chosen') return `Escrito a mano · ${deliveries}`;
  if (item.note === 'book') return `Del libro · ${deliveries}`;
  if (item.note === 'several') return 'Dos códigos distintos en el libro: elige uno';
  return item.note === 'hand' ? 'Fila añadida a mano' : 'Sin código en el libro';
}

/** IND-044: el texto del servidor dice qué pasa con el libro y qué hacer. */
export function bookErrorText(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'No se pudo leer el libro. Comprueba que es el Excel de recuento.';
}

export function filterSheetRows(rows: RecountSheetRow[], filter: RecountFilter, search: string): RecountSheetRow[] {
  return rows.filter((row) => {
    if (!matchesSearch(row, search)) return false;
    if (filter === 'pending') return row.current_qty === null;
    if (filter === 'counted') return row.current_qty !== null;
    if (filter === 'suggested') return hasSuggestion(row);
    if (filter === 'no_code') return row.supplier_state === 'no_code';
    if (filter === 'invalid') return row.invalid_code;
    if (filter === 'manual') return Object.values(row.origins).some((origin) => origin.source === 'tecleado');
    return true;
  });
}

/** Celdas que cambia «Usar las sugerencias»: lo tecleado no se pisa. */
export function suggestionTargets(rows: RecountSheetRow[], field: 'purchases_qty' | 'price'): RecountSheetRow[] {
  const suggested = field === 'price' ? 'price_suggested' : 'purchases_suggested';
  return rows.filter((row) => row[suggested] !== null && row[field] !== row[suggested] && !isTyped(row, field));
}

export function isFar(value: number | null, estimate: number | null | undefined): boolean {
  if (value === null || estimate === null || estimate === undefined) return false;
  const difference = Math.abs(value - estimate);
  return difference > FAR_MIN_QTY && difference > Math.abs(estimate) * FAR_SHARE;
}

export function tabletStatus(row: RecountSheetRow, estimate: RecountEstimate | undefined): TabletStatus {
  if (row.current_qty === null) return 'pending';
  if (isFar(row.current_qty, estimate?.estimate)) return 'far';
  return row.origins.current_qty?.source === 'copiado' ? 'copied' : 'counted';
}

export function isCopied(row: RecountSheetRow): boolean {
  return row.current_qty !== null && row.origins.current_qty?.source === 'copiado';
}

export function fmtQty(value: number | null | undefined, unit?: string): string {
  if (value === null || value === undefined) return '—';
  const text = qty.format(value);
  return unit === 'unidades' ? `${text} ud` : unit ? `${text} ${unit}` : text;
}

export type FinishCheck = { row: RecountSheetRow; title: string; detail: string };

/** «Revisa antes de terminar». Orientativo: no bloquea nada. */
export function finishChecks(
  rows: RecountSheetRow[],
  estimates: Record<string, RecountEstimate>,
): FinishCheck[] {
  const checks: FinishCheck[] = [];
  for (const row of rows) {
    const estimate = estimates[row.id];
    const title = [row.material_code, row.reference, row.supplier].filter(Boolean).join(' · ');
    const status = tabletStatus(row, estimate);
    if (status === 'far') {
      checks.push({
        row,
        title,
        detail: `Contado ${fmtQty(row.current_qty, row.unit)} · estimado por partes ≈ ${fmtQty(estimate?.estimate, row.unit)} · mes anterior ${fmtQty(row.previous_qty, row.unit)}`,
      });
    } else if (status === 'pending') {
      checks.push({ row, title, detail: `Pendiente · estimado ≈ ${fmtQty(estimate?.estimate, row.unit)}` });
    } else if (status === 'copied' && (estimate?.consumption ?? 0) > 0) {
      checks.push({
        row,
        title,
        detail: `Copiado ${fmtQty(row.current_qty, row.unit)}; según los partes el material tuvo consumo en el mes (orientativo)`,
      });
    }
  }
  return checks;
}

// Guardado en la tablet: lo que falta por llegar a PROA.
// IND-053: writtenAt = cuándo se escribió en la tablet (las colas antiguas no lo traen).
export type QueuedValue = { rowId: string; value: number | null; source: RecountRowSource; writtenAt?: string };

export function queueValue(queue: QueuedValue[], item: QueuedValue): QueuedValue[] {
  return [...queue.filter((entry) => entry.rowId !== item.rowId), item];
}

export function overlayQueue(rows: RecountSheetRow[], queue: QueuedValue[]): RecountSheetRow[] {
  if (!queue.length) return rows;
  const byRow = new Map(queue.map((item) => [item.rowId, item]));
  return rows.map((row) => {
    const item = byRow.get(row.id);
    if (!item) return row;
    return {
      ...row,
      current_qty: item.value,
      origins: { ...row.origins, current_qty: { source: item.source, by: null, at: null } },
    };
  });
}

function csvCell(value: string | number | null | undefined): string {
  if (typeof value === 'number') return `"${String(value)}"`;
  // Un valor que empiece por = + - @ no se ejecuta como fórmula en Excel.
  const text = String(value ?? '').normalize('NFKC');
  const safe = /^\s*[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

export const COMPARE_FIELDS: Record<string, string> = {
  current_qty: 'ACTUAL',
  purchases_qty: 'COMPRAS KG',
  price: 'PRECIO',
  consumption: 'CONSUMO',
};

export const COMPARE_KINDS: Record<string, string> = {
  different: 'Con diferencias',
  only_book: 'Solo en el libro',
  only_proa: 'Solo en PROA',
};

/** Con «;» y BOM para que Excel lo abra bien. */
export function compareCsv(result: RecountCompareResult): string {
  const line = (cells: Array<string | number | null | undefined>) => cells.map(csvCell).join(';');
  const lines = [line(['Caso', 'Código', 'Proveedor', 'Columna', 'PROA', 'Libro'])];
  for (const row of result.rows) {
    if (!row.differences.length) lines.push(line([COMPARE_KINDS[row.kind], row.material_code, row.supplier]));
    for (const item of row.differences) {
      lines.push(line([COMPARE_KINDS[row.kind], row.material_code, row.supplier, COMPARE_FIELDS[item.field], item.proa, item.book]));
    }
  }
  return `﻿${lines.join('\r\n')}`;
}
